// In-progress assistant indicator. Renders a synthesised bootstrap row
// the moment the user sends — before any WS event has landed — so the
// chat never looks dead. Once real subSteps arrive via the WS stream
// they replace the bootstrap and StepTimeline takes over.
//
// The "is it stuck?" UX lives inside StepTimeline (elapsed counter on
// the active row + softening copy at 20s / 60s). This component
// doesn't need its own watchdog — the elapsed counter ticks against
// the bootstrap row too, so a truly stalled run naturally surfaces as
// "Hmm, this is slow — you can keep waiting or send a new message"
// after a minute of silence, without ever framing as an error.

import { type FC } from 'react';
import { Box } from '@chakra-ui/react';
import { useAgentForgeStore } from '../../../stores';
import { StepTimeline, type TimelineStep } from '../StepTimeline';

// SpinnerSubStep matches StreamingSubStep in agentForgeStore. Keeping
// a local alias so callers don't need to import store types — the
// timeline is happy as long as the shape matches.
export interface SpinnerSubStep {
  id?: string;
  name: string;
  description?: string;
  status?: 'active' | 'done' | 'error';
  durationMs?: number;
}

interface SpinnerMessageProps {
  /** Optional one-line fallback used when no subSteps have arrived yet.
   *  Once any real subStep lands, the timeline replaces this. */
  detail?: string;
  /** Live timeline rows pushed by the WS stream. */
  subSteps?: SpinnerSubStep[];
  /** Kept for back-compat with ThreadBody; unused. */
  startedAt?: string;
  /** Kept for back-compat; ignored. */
  label?: string;
}

const toTimelineSteps = (subSteps: SpinnerSubStep[]): TimelineStep[] =>
  subSteps.map((s, i) => ({
    id: s.id ?? `step-${i}`,
    name: s.name,
    description: s.description,
    status: s.status ?? 'active',
    durationMs: s.durationMs,
  }));

export const SpinnerMessage: FC<SpinnerMessageProps> = ({
  detail,
  subSteps,
}) => {
  // Claude-Code-style "↓ tokens" signal: ~4 chars/token over the live
  // draft's streamed prose. Zero until the first delta lands, so the
  // pre-stream bootstrap row stays clean.
  const approxDownTokens = useAgentForgeStore((s) =>
    Math.round((s.streamingDraft?.content.length ?? 0) / 4)
  );
  const steps = toTimelineSteps(subSteps ?? []);
  const effective: TimelineStep[] =
    steps.length > 0
      ? steps
      : [
          {
            id: 'bootstrap',
            name: detail || 'Thinking…',
            status: 'active',
          },
        ];
  return (
    <Box role="status" aria-live="polite">
      <StepTimeline
        steps={effective}
        streaming
        approxDownTokens={approxDownTokens}
      />
    </Box>
  );
};
